export const SUPPORT_LANGUAGES = ["English", "Spanish", "Korean"];

const SNAPSHOT_LOOKUP_PATH = "/v1/dreamcast/support/snapshot/lookup";
const MANUAL_REPAIRS_PATH = "/v1/dreamcast/manual-repairs";
const SETTINGS_LOOKUP_PATH = "/v1/dreamcast/user/settings/lookup";
const SETTINGS_UPDATE_PATH = "/v1/dreamcast/user/settings";
const SESSION_REVOCATION_PATH = "/v1/dreamcast/user/sessions/revoke";
const CONTACT_ROLES = ["primary", "co"];
const LEGACY_ACCOUNT_STATUSES = ["available", "missing"];
const ACCOUNT_STATUSES = ["available", "pending", "unavailable"];
const DEPENDENCY_STATUSES = ["ok", "failed", "stale", "missing"];
const DEPENDENCY_KEYS = [
  "account",
  "eligibility",
  "discrepancy",
  "history",
  "freshness",
];
const MANUAL_REPAIR_STATUSES = [
  "pending",
  "retry-scheduled",
  "processing",
  "completed",
  "ineligible",
  "conflict",
  "dead",
  "canceled",
];

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isNullableNonEmptyString(value) {
  return value == null || isNonEmptyString(value);
}

function isIsoString(value) {
  return isNonEmptyString(value) && !Number.isNaN(Date.parse(value));
}

function isNullableIsoString(value) {
  return value == null || isIsoString(value);
}

function normalizeDependency(dependency) {
  if (!isObject(dependency)) {
    return null;
  }

  if (!DEPENDENCY_STATUSES.includes(dependency.status)) {
    return null;
  }

  if (dependency.message != null && !isNonEmptyString(dependency.message)) {
    return null;
  }

  return {
    status: dependency.status,
    message: dependency.message == null ? null : dependency.message,
  };
}

function normalizeDependenciesByRole(roleDependencies) {
  if (!isObject(roleDependencies)) {
    return null;
  }

  const normalized = {};
  for (const dependencyKey of DEPENDENCY_KEYS) {
    const dependency = normalizeDependency(roleDependencies[dependencyKey]);
    if (!dependency) {
      return null;
    }
    normalized[dependencyKey] = dependency;
  }

  return normalized;
}

function normalizeHistoryEntry(entry) {
  if (!isObject(entry)) {
    return null;
  }

  if (
    !isNonEmptyString(entry.id) ||
    !isIsoString(entry.happenedAt) ||
    !isNonEmptyString(entry.event) ||
    !isNonEmptyString(entry.source)
  ) {
    return null;
  }

  return {
    id: entry.id,
    happenedAt: entry.happenedAt,
    event: entry.event,
    source: entry.source,
  };
}

function normalizeHistoryPaginationRole(paginationRole) {
  if (!isObject(paginationRole)) {
    return null;
  }

  if (
    !Number.isSafeInteger(paginationRole.returnedCount) ||
    paginationRole.returnedCount < 0
  ) {
    return null;
  }

  if (typeof paginationRole.truncated !== "boolean") {
    return null;
  }

  if ("continuation" in paginationRole) {
    return null;
  }

  return {
    returnedCount: paginationRole.returnedCount,
    truncated: paginationRole.truncated,
  };
}

function normalizeDiscrepancy(discrepancy, discrepancyDependencyStatus) {
  if (!isObject(discrepancy)) {
    return null;
  }

  if (!Array.isArray(discrepancy.details)) {
    return null;
  }

  if (!discrepancy.details.every((detail) => isNonEmptyString(detail))) {
    return null;
  }

  if (discrepancyDependencyStatus === "ok") {
    if (typeof discrepancy.hasDiscrepancy !== "boolean") {
      return null;
    }

    return {
      hasDiscrepancy: discrepancy.hasDiscrepancy,
      details: discrepancy.details,
    };
  }

  if (discrepancy.hasDiscrepancy !== null) {
    return null;
  }

  return {
    hasDiscrepancy: null,
    details: discrepancy.details,
  };
}

function normalizeContact(contact, role, roleDependencies) {
  if (contact == null) {
    return null;
  }

  if (!isObject(contact) || contact.role !== role) {
    return null;
  }

  if (
    !isNonEmptyString(contact.displayName) ||
    !isNonEmptyString(contact.iboNumber) ||
    !isNonEmptyString(contact.wwgId) ||
    !isNonEmptyString(contact.email)
  ) {
    return null;
  }

  if (!isObject(contact.account)) {
    return null;
  }

  if (!ACCOUNT_STATUSES.includes(contact.account.status)) {
    return null;
  }

  if (!isNonEmptyString(contact.account.reason)) {
    return null;
  }

  if (
    contact.account.dreamCastId != null &&
    !isNonEmptyString(contact.account.dreamCastId)
  ) {
    return null;
  }

  if (!isNullableNonEmptyString(contact.account.provisioningStatus)) {
    return null;
  }

  if (!isNullableIsoString(contact.account.lastSyncedAt)) {
    return null;
  }

  if (!isObject(contact.eligibility)) {
    return null;
  }

  if (
    typeof contact.eligibility.isEligible !== "boolean" ||
    !isNonEmptyString(contact.eligibility.reason)
  ) {
    return null;
  }

  const discrepancyDependencyStatus = roleDependencies.discrepancy.status;
  const discrepancy = normalizeDiscrepancy(
    contact.discrepancy,
    discrepancyDependencyStatus
  );
  if (!discrepancy) {
    return null;
  }

  if (!Array.isArray(contact.history)) {
    return null;
  }

  const history = contact.history.map(normalizeHistoryEntry);
  if (history.some((entry) => !entry)) {
    return null;
  }

  return {
    role,
    displayName: contact.displayName,
    iboNumber: contact.iboNumber,
    wwgId: contact.wwgId,
    email: contact.email,
    account: {
      status: contact.account.status,
      reason: contact.account.reason,
      dreamCastId:
        contact.account.dreamCastId == null
          ? null
          : contact.account.dreamCastId,
      provisioningStatus:
        contact.account.provisioningStatus == null
          ? null
          : contact.account.provisioningStatus,
      lastSyncedAt:
        contact.account.lastSyncedAt == null
          ? null
          : contact.account.lastSyncedAt,
    },
    eligibility: {
      isEligible: contact.eligibility.isEligible,
      reason: contact.eligibility.reason,
    },
    discrepancy,
    history,
  };
}

function parseStrictDreamCastSnapshot(payload) {
  if (!isObject(payload)) {
    return null;
  }

  if (!Number.isSafeInteger(payload.pageSize) || payload.pageSize <= 0) {
    return null;
  }

  if (!isIsoString(payload.fetchedAt)) {
    return null;
  }

  if (
    !Array.isArray(payload.partialFailures) ||
    !payload.partialFailures.every((failure) => isNonEmptyString(failure))
  ) {
    return null;
  }

  if (!isObject(payload.dependencies) || !isObject(payload.contacts)) {
    return null;
  }

  if (!isObject(payload.historyPagination)) {
    return null;
  }

  if (payload.historyPagination.mode !== "bounded-history-v2") {
    return null;
  }

  if (
    !Number.isSafeInteger(payload.historyPagination.maxItems) ||
    payload.historyPagination.maxItems <= 0
  ) {
    return null;
  }

  if ("continuation" in payload.historyPagination) {
    return null;
  }

  const dependencies = {};
  const contacts = {};
  const historyPagination = {
    mode: payload.historyPagination.mode,
    maxItems: payload.historyPagination.maxItems,
  };

  for (const role of CONTACT_ROLES) {
    const roleDependencies = normalizeDependenciesByRole(
      payload.dependencies[role]
    );
    if (!roleDependencies) {
      return null;
    }
    dependencies[role] = roleDependencies;

    const rolePagination = normalizeHistoryPaginationRole(
      payload.historyPagination[role]
    );
    if (!rolePagination) {
      return null;
    }
    historyPagination[role] = rolePagination;

    const contact = normalizeContact(
      payload.contacts[role],
      role,
      roleDependencies
    );
    if (payload.contacts[role] != null && !contact) {
      return null;
    }

    const historyLength = contact ? contact.history.length : 0;
    if (rolePagination.returnedCount !== historyLength) {
      return null;
    }

    contacts[role] = contact;
  }

  return {
    pageSize: payload.pageSize,
    fetchedAt: payload.fetchedAt,
    partialFailures: payload.partialFailures,
    dependencies,
    historyPagination,
    contacts,
  };
}

function parseLegacyDreamCastSnapshot(payload) {
  if (!isObject(payload)) {
    return null;
  }

  if (!Number.isSafeInteger(payload.pageSize) || payload.pageSize <= 0) {
    return null;
  }

  if (!isIsoString(payload.fetchedAt)) {
    return null;
  }

  if (
    !Array.isArray(payload.partialFailures) ||
    !payload.partialFailures.every((failure) => isNonEmptyString(failure))
  ) {
    return null;
  }

  if (!isObject(payload.dependencies) || !isObject(payload.contacts)) {
    return null;
  }

  const dependencies = {};
  const contacts = {};
  const historyPagination = {
    mode: "bounded-history-v2",
    maxItems: payload.pageSize,
  };

  for (const role of CONTACT_ROLES) {
    const roleDependencies = normalizeDependenciesByRole(
      payload.dependencies[role]
    );
    if (!roleDependencies) {
      return null;
    }
    dependencies[role] = roleDependencies;

    const contact = payload.contacts[role];
    if (contact == null) {
      contacts[role] = null;
      historyPagination[role] = {
        returnedCount: 0,
        truncated: false,
      };
      continue;
    }

    if (
      !isObject(contact) ||
      contact.role !== role ||
      !isObject(contact.account)
    ) {
      return null;
    }

    if (!LEGACY_ACCOUNT_STATUSES.includes(contact.account.status)) {
      return null;
    }

    const accountStatus =
      contact.account.status === "missing" ? "unavailable" : "available";

    const normalizedContact = normalizeContact(
      {
        ...contact,
        account: {
          status: accountStatus,
          reason:
            accountStatus === "available"
              ? "DreamCast account is available."
              : "DreamCast account is unavailable.",
          dreamCastId: contact.account.dreamCastId,
          provisioningStatus:
            accountStatus === "available" ? "completed" : "unavailable",
          lastSyncedAt: contact.account.lastSyncedAt,
        },
        discrepancy:
          roleDependencies.discrepancy.status === "ok"
            ? contact.discrepancy
            : null,
      },
      role,
      roleDependencies
    );

    if (!normalizedContact) {
      return null;
    }

    contacts[role] = normalizedContact;
    historyPagination[role] = {
      returnedCount: normalizedContact.history.length,
      truncated: false,
    };
  }

  return {
    pageSize: payload.pageSize,
    fetchedAt: payload.fetchedAt,
    partialFailures: payload.partialFailures,
    dependencies,
    historyPagination,
    contacts,
  };
}

export function parseDreamCastSnapshot(
  payload,
  { allowLegacyFixture = false } = {}
) {
  const strictSnapshot = parseStrictDreamCastSnapshot(payload);
  if (strictSnapshot) {
    return strictSnapshot;
  }

  if (!allowLegacyFixture) {
    return null;
  }

  return parseLegacyDreamCastSnapshot(payload);
}

export function parseManualRepairStatus(payload) {
  if (!isObject(payload)) {
    return null;
  }

  if (!isNonEmptyString(payload.operationId)) {
    return null;
  }

  if (!MANUAL_REPAIR_STATUSES.includes(payload.status)) {
    return null;
  }

  if (typeof payload.coalesced !== "boolean") {
    return null;
  }

  if (!isIsoString(payload.createdOn) || !isIsoString(payload.modifiedOn)) {
    return null;
  }

  if (!isNonEmptyString(payload.statusUrl)) {
    return null;
  }

  return {
    operationId: payload.operationId,
    status: payload.status,
    coalesced: payload.coalesced,
    createdOn: payload.createdOn,
    modifiedOn: payload.modifiedOn,
    statusUrl: payload.statusUrl,
  };
}

export function parseSupportSettings(payload) {
  if (!isObject(payload)) {
    return null;
  }

  if (typeof payload.receiveNotifications !== "boolean") {
    return null;
  }

  if (
    payload.language !== null &&
    !SUPPORT_LANGUAGES.includes(payload.language)
  ) {
    return null;
  }

  return {
    receiveNotifications: payload.receiveNotifications,
    language: payload.language,
  };
}

export function parseSessionRevocation(payload) {
  if (
    !isObject(payload) ||
    !Number.isSafeInteger(payload.revokedSessionCount) ||
    payload.revokedSessionCount < 0
  ) {
    return null;
  }

  return { revokedSessionCount: payload.revokedSessionCount };
}

async function requestJson(makeAuthedRequest, path, options, parsePayload) {
  try {
    const response = await makeAuthedRequest(path, options);
    if (!response.ok) {
      return { ok: false, status: response.status };
    }

    let payload;
    try {
      payload = await response.json();
    } catch (error) {
      return { ok: false, status: response.status, invalidPayload: true };
    }

    const data = parsePayload(payload);
    if (!data) {
      return { ok: false, status: response.status, invalidPayload: true };
    }

    return { ok: true, status: response.status, data };
  } catch (error) {
    return { ok: false, status: null };
  }
}

export function lookupSupportSettings(makeAuthedRequest, email) {
  return requestJson(
    makeAuthedRequest,
    SETTINGS_LOOKUP_PATH,
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
    parseSupportSettings
  );
}

export function updateSupportSettings(makeAuthedRequest, email, changes) {
  return requestJson(
    makeAuthedRequest,
    SETTINGS_UPDATE_PATH,
    {
      method: "PATCH",
      body: JSON.stringify({ email, changes }),
    },
    parseSupportSettings
  );
}

export function revokeSupportSessions(makeAuthedRequest, email) {
  return requestJson(
    makeAuthedRequest,
    SESSION_REVOCATION_PATH,
    {
      method: "POST",
      body: JSON.stringify({
        email,
        confirmation: "REVOKE_ALL_SESSIONS",
      }),
    },
    parseSessionRevocation
  );
}

export function lookupDreamCastSupportSnapshot(
  makeAuthedRequest,
  iboNumber,
  { allowLegacyFixture = false } = {}
) {
  return requestJson(
    makeAuthedRequest,
    SNAPSHOT_LOOKUP_PATH,
    {
      method: "POST",
      body: JSON.stringify({ iboNumber }),
    },
    (payload) =>
      parseDreamCastSnapshot(payload, {
        allowLegacyFixture,
      })
  );
}

export function createDreamCastManualRepair(makeAuthedRequest, requestBody) {
  return requestJson(
    makeAuthedRequest,
    MANUAL_REPAIRS_PATH,
    {
      method: "POST",
      body: JSON.stringify(requestBody),
    },
    parseManualRepairStatus
  );
}

export function lookupDreamCastManualRepairStatus(
  makeAuthedRequest,
  operationId
) {
  if (!isNonEmptyString(operationId)) {
    return Promise.resolve({ ok: false, status: null, invalidPayload: true });
  }

  const encodedOperationId = encodeURIComponent(operationId);
  return requestJson(
    makeAuthedRequest,
    `${MANUAL_REPAIRS_PATH}/${encodedOperationId}`,
    {
      method: "GET",
    },
    parseManualRepairStatus
  );
}
